import { EOperationType, TOperationSetting } from '_c/typings/model/chat/message-operate';
import { IMessageViewConfig, MessageConfig } from '@/business/chat/generate-message-config';
import { UserManager } from '_c/business/manager/user';
import { GroupUserManager } from '_c/business/manager/group-user';
import { MsgL2Type } from '_c/typings/proto';
import { StickerManager } from '_c/business/manager/sticker';
import { TPosition, TPositionOffset } from '_c/typings/model/position';
import { reactionConfig } from './reaction-config';
import { getInViewPosition } from './position-util';
import intersectionWith from 'lodash/intersectionWith';

/**
 * 操作配置表
 */
export const operationSettingMap: Record<EOperationType, TOperationSetting> = {
  [EOperationType.reaction]: {
    type: EOperationType.reaction,
    text: '表态',
    icon: 'operate-reaction',
    order: 0,
    hasHoverRightArrow: true,
  },
  [EOperationType.reply]: {
    type: EOperationType.reply,
    text: '回复',
    icon: 'operate-reply',
    order: 1,
  },
  [EOperationType.top]: {
    type: EOperationType.top,
    text: '置顶',
    icon: 'operate-top',
    order: 2,
  },
  [EOperationType.copy]: {
    type: EOperationType.copy,
    text: '复制',
    icon: 'operate-copy',
    order: 3,
  },
  [EOperationType.cancel]: {
    type: EOperationType.cancel,
    text: '撤回',
    icon: 'operate-cancel',
    order: 4,
  },
  [EOperationType.report]: {
    type: EOperationType.report,
    text: '举报',
    icon: 'operate-report',
    hasCutOffLine: true,
    order: 5,
  },
  [EOperationType.multiSelect]: {
    type: EOperationType.multiSelect,
    text: '多选',
    icon: 'multi-select',
  },
  [EOperationType.share]: {
    type: EOperationType.share,
    text: '分享',
    icon: 'operate-share',
  },
  [EOperationType.add]: {
    type: EOperationType.add,
    text: '添加',
    icon: 'operate-add',
  },
  [EOperationType.position]: {
    type: EOperationType.position,
    text: '位置',
    icon: 'operate-position',
  },
};

/**
 * 获取消息的操作列表
 * @param messageConfig
 * @param gid
 * @param cid
 */
export const getMessageOperateList = ({
  gid,
  messageConfig,
  messageViewConfig,
}: {
  gid?: string;
  cid?: string;
  messageConfig: MessageConfig;
  messageViewConfig?: IMessageViewConfig;
}): Array<TOperationSetting> => {
  const { supportOperateList } = messageConfig;
  if (supportOperateList?.length === 0) return [];
  const currentUid = Number(UserManager.getInstance().getCurrentUid());
  const isAdmin = GroupUserManager.getInstance().isCurrentUserGroupChannelAdmin(String(gid));
  const messageSenderUid = Number(messageConfig.messageData.senderUid);
  let tempList: Array<TOperationSetting> = [];
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  for (const item of supportOperateList!) {
    if (item.type === EOperationType.cancel) {
      const canCancel = new Date().getTime() - (Number(messageConfig.messageData.ts) * 1000) < 3 * 60 * 1000;
      // 超时或者不是本人消息, 过滤掉撤回
      if (currentUid === messageSenderUid && canCancel) {
        tempList.push(item);
      }
    } else if (item.type === EOperationType.report) {
      // 本人不显示举报
      if (currentUid !== messageSenderUid) {
        tempList.push(item);
      }
    } else if (item.type === EOperationType.top) {
      // 置顶必须只有管理员可以操作
      if (isAdmin) {
        tempList.push(item);
      }
    } else if (item.type === EOperationType.add) {
      // 涂鸦、图片和表情消息要判断一下当前有没有相同的表情，如果有，需要过滤
      let showFlag = false;
      const { l2Type } = messageConfig.messageData;
      const isMessagePic = l2Type === MsgL2Type.MsgL2TypePic;
      const isMessageGraffiti = l2Type === MsgL2Type.MsgL2TypeGraffiti;
      const isMessageSticker = l2Type === MsgL2Type.MsgL2TypeSticker;
      if (isMessagePic || isMessageSticker || isMessageGraffiti) {
        const list = StickerManager.getInstance().stickerList;
        let md5 = isMessagePic
          ? messageConfig.messageData?.picInfo?.[0]?.imageInfoArray?.[0]?.md5sum : messageConfig.messageData?.stickerMsg?.md5sum ?? '';

        if (messageConfig?.messageData?.graffitiMsg) {
          md5 = isMessageGraffiti
            ? messageConfig.messageData.graffitiMsg.picInfo?.[0]?.imageInfoArray?.[0]?.md5sum : messageConfig.messageData?.stickerMsg?.md5sum ?? '';
        }
        for (const item of list) {
          if (item.md5 === md5) {
            showFlag = true;
            break;
          }
        }
      }
      if (!showFlag) {
        tempList.push(item);
      }
    } else {
      tempList.push(item);
    }
  }
  if (messageViewConfig?.supportOperateList) {
    // 取交集
    tempList = intersectionWith(
      tempList,
      messageViewConfig?.supportOperateList,
      (item1, item2) => item1.type === item2.type,
    );
  }
  const canReviseReaction = messageViewConfig?.canReviseReaction ?? messageConfig?.canReviseReaction ?? true;
  // 如果不能对 reaction 进行增删操作，那么就去除 reaction
  if (!canReviseReaction && tempList.length) {
    const index = tempList.findIndex(item => item.type === EOperationType.reaction);
    if (index >= 0) {
      tempList.splice(index, 1);
    }
  }
  tempList.sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0));
  return tempList;
};

export interface OperateStyleSize {
  optionHeight: number,
  splitHeight: number,
  paddingTop: number,
  paddingBottom: number,
  popupWidth: number
}

/**
 * 操作列表尺寸
 */
export const operateStyleSize: OperateStyleSize = {
  optionHeight: 33,
  splitHeight: 17,
  paddingTop: 6,
  paddingBottom: 6,
  popupWidth: 166,
};

/**
 * 获取操作列表尺寸
 * @param arr
 */
export const getOperatePopupSize = (arr: Array<TOperationSetting>): { width: number, height: number } => {
  let totalHeight = operateStyleSize.paddingTop + operateStyleSize.paddingBottom;
  arr.forEach((item) => {
    if (item.hasCutOffLine) {
      totalHeight += operateStyleSize.splitHeight;
    }
    totalHeight += operateStyleSize.optionHeight;
  });

  return {
    width: operateStyleSize.popupWidth,
    height: totalHeight,
  };
};

/**
 * 获取操作面板的位置
 * @param event 鼠标事件
 * @param settingList 操作面板配置列表
 * @param positionOffset 位置偏差值
 * @returns 操作面板的位置
 */
export function getOperatePopupPosition(
  event: MouseEvent,
  settingList: TOperationSetting[],
  positionOffset?: TPositionOffset,
): TPosition {
  const { clientX, clientY } = event;
  const { width, height } = getOperatePopupSize(settingList);
  const hasReaction = settingList.filter(item => item.type === EOperationType.reaction).length > 0;
  const originPosition = {
    x: clientX,
    y: clientY,
    width,
    height,
  };
  if (hasReaction) {
    const { offsetWidth = 0, offsetHeight = 0 } = positionOffset || {};
    const { popupWidth, popupHeight } = reactionConfig;
    return getInViewPosition(originPosition, {
      ...positionOffset,
      offsetWidth: offsetWidth + popupWidth,
      offsetHeight: offsetHeight + Math.max(popupHeight - height, 0),
    });
  }

  return getInViewPosition(originPosition, positionOffset);
}
